/**
 * Segments — saved audience filters, with a live preview.
 *
 * The available fields and their types come from the backend
 * (GET /mailing/segments/fields) rather than being listed here: the operator
 * set depends on the type, and the backend's whitelist is what actually decides
 * whether a condition survives. A local copy would drift on the first change.
 */
define('mailingSegmentsView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  MailingApi
) {
  'use strict';

  /** Operators offered per field type — must stay a subset of the backend's. */
  var OPERATORS = {
    text: [
      { key: 'equals', label: 'is' },
      { key: 'not_equals', label: 'is not' },
      { key: 'contains', label: 'contains' },
      { key: 'not_contains', label: 'does not contain' },
      { key: 'starts_with', label: 'starts with' },
      { key: 'is_empty', label: 'is empty', noValue: true },
      { key: 'is_not_empty', label: 'is not empty', noValue: true }
    ],
    number: [
      { key: 'equals', label: '=' },
      { key: 'not_equals', label: '≠' },
      { key: 'gt', label: '>' },
      { key: 'gte', label: '≥' },
      { key: 'lt', label: '<' },
      { key: 'lte', label: '≤' }
    ],
    'boolean': [
      { key: 'is_true', label: 'is yes', noValue: true },
      { key: 'is_false', label: 'is no', noValue: true }
    ],
    // Membership of another segment: "customers who are NOT in 'Contacted this month'".
    segment: [
      { key: 'in_segment', label: 'is in segment', valueType: 'segment' },
      { key: 'not_in_segment', label: 'is not in segment', valueType: 'segment' }
    ],
    date: [
      { key: 'after', label: 'after', valueType: 'date' },
      { key: 'before', label: 'before', valueType: 'date' },
      { key: 'within_last', label: 'within the last', valueType: 'duration' },
      { key: 'more_than_ago', label: 'more than … ago', valueType: 'duration' },
      { key: 'is_empty', label: 'is never', noValue: true },
      { key: 'is_not_empty', label: 'is set', noValue: true }
    ]
  };

  var SegmentsView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-segments',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-body"><div class="mlg-loading">Loading segments…</div></div>' +
      '<div class="mlg-modal" style="display:none"></div>'
    ),

    ui: {
      body: '.mlg-body',
      modal: '.mlg-modal'
    },

    events: {
      'click .mlg-new-segment': 'onNew',
      'click .mlg-audience-check': 'onAudienceCheck',
      'click .mlg-edit': 'onEdit',
      'click .mlg-delete': 'onDelete',
      'click .mlg-modal-cancel': 'closeModal',
      'click .mlg-modal-save': 'onSave',
      'click .mlg-add-condition': 'onAddCondition',
      'click .mlg-remove-condition': 'onRemoveCondition',
      'change .f-field': 'onFieldChanged',
      'change .f-operator': 'onOperatorChanged',
      'change .f-logic': 'schedulePreview',
      'change .c-value, .c-unit': 'schedulePreview',
      'click .mlg-show-all': 'onShowAll',
      'keyup .c-value': 'schedulePreview'
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('segments',
          '<button type="button" class="mlg-btn mlg-audience-check" title="Who each segment sends to from the ' +
            'contact list, and who it would send to from the customer file">Audience check</button>' +
          '<button type="button" class="mlg-btn primary mlg-new-segment">New segment</button>')
      };
    },

    onShow: function () {
      this.load();
    },

    load: function () {
      var self = this;
      $.when(MailingApi.segments(), MailingApi.segmentFields())
        .done(function (segments, fields) {
          self.fields = fields || [];
          self.segments = segments || [];
          self.renderList(self.segments);
        })
        .fail(function (xhr) {
          self.ui.body.html('<div class="mlg-error">' +
            _.escape(MailingApi.errorMessage(xhr)) + '</div>');
        });
    },

    renderList: function (segments) {
      if (!segments.length) {
        this.ui.body.html(Shell.emptyHtml(
          'No segment yet. A segment is the audience a campaign or a workflow sends to.'));
        return;
      }
      var rows = _.map(segments, function (segment) {
        return '<tr data-secid="' + segment.secId + '">' +
          '<td class="c-name">' + _.escape(segment.name || '') +
            '<div class="c-sub">' + _.escape(segment.description || '') + '</div></td>' +
          '<td class="num">' + Shell.formatNumber(segment.subscriberCount) + '</td>' +
          '<td class="c-actions">' +
          '  <button type="button" class="mlg-btn small mlg-edit">Edit</button>' +
          '  <button type="button" class="mlg-btn small danger mlg-delete">Delete</button>' +
          '</td></tr>';
      }).join('');

      this.ui.body.html(
        '<table class="mlg-table">' +
        '<thead><tr><th>Segment</th>' +
        '<th class="num" title="Active contacts at the last nightly refresh. A send always ' +
          'resolves its audience live, so this number is indicative.">Contacts</th>' +
        '<th></th></tr></thead>' +
        '<tbody>' + rows + '</tbody></table>');
    },

    //----------Audience check-----------------------------------------------//

    /**
     * Dry run of the switch to the audience read from the customer file
     * (backend flag MAILING_AUDIENCE_FROM_ENDUSER): per segment, the contacts
     * the synced list sends to, those the customer file would, and who is only
     * on one side. Read it before the switch, and after editing a segment.
     */
    onAudienceCheck: function () {
      var self = this;
      Shell.banner(this.$el, 'Comparing the audiences — this can take a moment…', 'ok');
      MailingApi.audienceCheck().done(function (result) {
        function sample(emails, total) {
          if (!total) { return '—'; }
          return '<details><summary>' + Shell.formatNumber(total) + '</summary>' +
            '<div class="c-sub">' + _.map(emails || [], _.escape).join('<br>') +
            (total > (emails || []).length ? '<br>…' : '') + '</div></details>';
        }
        var rows = _.map(result.segments || [], function (row) {
          return '<tr>' +
            '<td class="c-name">' + (row.name ? _.escape(row.name) : '<em>All contacts</em>') + '</td>' +
            '<td class="num">' + Shell.formatNumber(row.legacy) + '</td>' +
            '<td class="num">' + Shell.formatNumber(row.fromEndUser) + '</td>' +
            '<td>' + sample(row.onlyLegacySample, row.onlyLegacy) + '</td>' +
            '<td>' + sample(row.onlyFromEndUserSample, row.onlyFromEndUser) + '</td>' +
            '</tr>';
        }).join('');
        self.ui.modal.html(
          '<div class="mlg-modal-card wide">' +
          '<h3>Audience check</h3>' +
          '<div class="mlg-hint">Currently sending from: <b>' +
            (result.audienceFromEndUser ? 'the customer file' : 'the synced contact list') + '</b>. ' +
            'Active contacts only. Leads are only counted in segments that include them.</div>' +
          '<table class="mlg-table compact">' +
          '<thead><tr><th>Segment</th><th class="num">Contact list</th><th class="num">Customer file</th>' +
          '<th>Only in the contact list</th><th>Only in the customer file</th></tr></thead>' +
          '<tbody>' + rows + '</tbody></table>' +
          '<div class="mlg-modal-actions">' +
          '  <button type="button" class="mlg-btn mlg-modal-cancel">Close</button>' +
          '</div>' +
          '</div>').show();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    //----------Editor-------------------------------------------------------//

    onNew: function () {
      this.openEditor({ filters: '{"logic":"AND","conditions":[]}' });
    },

    onEdit: function (e) {
      var self = this;
      MailingApi.segment(this.secIdOf(e)).done(function (segment) {
        self.openEditor(segment);
      });
    },

    openEditor: function (segment) {
      var filters = this.parseFilters(segment.filters);
      this.editingSecId = segment.secId || null;

      this.ui.modal.html(
        '<div class="mlg-modal-card wide">' +
        '<h3>' + (segment.secId ? 'Edit segment' : 'New segment') + '</h3>' +
        '<div class="mlg-row">' +
        '  <label>Name<input type="text" class="f-name" value="' + _.escape(segment.name || '') + '"></label>' +
        '  <label>Description<input type="text" class="f-description" value="' +
             _.escape(segment.description || '') + '"></label>' +
        '</div>' +
        '<div class="mlg-filter-head">' +
        '  <label class="inline">Match<select class="f-logic">' +
        '    <option value="AND"' + (filters.logic === 'OR' ? '' : ' selected') + '>all conditions</option>' +
        '    <option value="OR"' + (filters.logic === 'OR' ? ' selected' : '') + '>any condition</option>' +
        '  </select></label>' +
        '  <button type="button" class="mlg-btn small mlg-add-condition">Add condition</button>' +
        '</div>' +
        '<div class="mlg-conditions"></div>' +
        '<div class="mlg-preview">Preview: <span class="p-count">—</span> ' +
        '  <button type="button" class="mlg-btn small mlg-show-all" style="display:none">Show all contacts</button></div>' +
        '<div class="mlg-preview-sample"></div>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-modal-save">Save</button>' +
        '</div>' +
        '</div>').show();

      var $conditions = this.ui.modal.find('.mlg-conditions');
      _.each(filters.conditions, function (condition) {
        $conditions.append(this.conditionHtml(condition));
      }, this);

      this.schedulePreview();
    },

    parseFilters: function (raw) {
      try {
        var parsed = JSON.parse(raw || '{}');
        return {
          logic: parsed.logic === 'OR' ? 'OR' : 'AND',
          conditions: _.isArray(parsed.conditions) ? parsed.conditions : []
        };
      } catch (error) {
        return { logic: 'AND', conditions: [] };
      }
    },

    conditionHtml: function (condition) {
      var current = condition || {};
      var fieldName = current.field || (this.fields.length ? this.fields[0].name : '');
      var field = _.findWhere(this.fields, { name: fieldName }) || this.fields[0] || { type: 'text' };

      var fieldOptions = _.map(this.fields, function (item) {
        return '<option value="' + item.name + '"' + (item.name === fieldName ? ' selected' : '') + '>' +
          _.escape(Shell.prettyCase(item.name)) + '</option>';
      }).join('');

      return '<div class="mlg-condition" data-type="' + field.type + '">' +
        '<select class="f-field">' + fieldOptions + '</select>' +
        '<select class="f-operator">' + this.operatorOptions(field.type, current.operator) + '</select>' +
        this.valueHtml(field.type, current.operator, current.value, current.unit) +
        '<button type="button" class="mlg-icon-btn mlg-remove-condition" title="Remove">×</button>' +
        '</div>';
    },

    operatorOptions: function (type, selected) {
      return _.map(OPERATORS[type] || OPERATORS.text, function (operator) {
        return '<option value="' + operator.key + '"' +
          (operator.key === selected ? ' selected' : '') + '>' +
          _.escape(operator.label) + '</option>';
      }).join('');
    },

    /** The value input depends on the operator: none, a date, a number, or a duration. */
    valueHtml: function (type, operatorKey, value, unit) {
      var operators = OPERATORS[type] || OPERATORS.text;
      var operator = _.findWhere(operators, { key: operatorKey }) || operators[0];
      if (!operator || operator.noValue) {
        return '<span class="c-value-slot"></span>';
      }
      if (operator.valueType === 'date') {
        return '<span class="c-value-slot"><input type="date" class="c-value" value="' +
          _.escape(value || '') + '"></span>';
      }
      if (operator.valueType === 'segment') {
        // The segment being edited is left out: a segment defined by itself is a loop.
        var editing = this.editingSecId;
        var options = _.map(_.reject(this.segments || [], function (segment) { return segment.secId === editing; }),
          function (segment) {
            return '<option value="' + segment.secId + '"' + (segment.secId === value ? ' selected' : '') + '>' +
              _.escape(segment.name || '') + ' (' + Shell.formatNumber(segment.subscriberCount) + ')</option>';
          }).join('');
        return '<span class="c-value-slot"><select class="c-value">' + (options || '<option value="">(no other segment)</option>') +
          '</select></span>';
      }
      if (operator.valueType === 'duration') {
        var units = ['days', 'weeks', 'months'];
        var unitOptions = _.map(units, function (item) {
          return '<option value="' + item + '"' + (item === unit ? ' selected' : '') + '>' + item + '</option>';
        }).join('');
        return '<span class="c-value-slot"><input type="number" min="1" class="c-value narrow" value="' +
          _.escape(value || '1') + '"><select class="c-unit">' + unitOptions + '</select></span>';
      }
      return '<span class="c-value-slot"><input type="' + (type === 'number' ? 'number' : 'text') +
        '" class="c-value" value="' + _.escape(value === undefined ? '' : value) + '"></span>';
    },

    onAddCondition: function () {
      this.ui.modal.find('.mlg-conditions').append(this.conditionHtml(null));
      this.schedulePreview();
    },

    onRemoveCondition: function (e) {
      $(e.currentTarget).closest('.mlg-condition').remove();
      this.schedulePreview();
    },

    /** Field change resets the operator list — an operator rarely survives a type change. */
    onFieldChanged: function (e) {
      var $condition = $(e.currentTarget).closest('.mlg-condition');
      var field = _.findWhere(this.fields, { name: $condition.find('.f-field').val() }) || { type: 'text' };
      $condition.attr('data-type', field.type);
      $condition.find('.f-operator').html(this.operatorOptions(field.type, null));
      $condition.find('.c-value-slot').replaceWith(
        this.valueHtml(field.type, $condition.find('.f-operator').val(), '', 'days'));
      this.schedulePreview();
    },

    onOperatorChanged: function (e) {
      var $condition = $(e.currentTarget).closest('.mlg-condition');
      var type = $condition.attr('data-type') || 'text';
      $condition.find('.c-value-slot').replaceWith(
        this.valueHtml(type, $condition.find('.f-operator').val(), '', 'days'));
      this.schedulePreview();
    },

    //----------Preview------------------------------------------------------//

    collectFilters: function () {
      var conditions = [];
      this.ui.modal.find('.mlg-condition').each(function () {
        var $condition = $(this);
        var condition = {
          field: $condition.find('.f-field').val(),
          operator: $condition.find('.f-operator').val()
        };
        var $value = $condition.find('.c-value');
        if ($value.length) {
          condition.value = $value.val();
        }
        var $unit = $condition.find('.c-unit');
        if ($unit.length) {
          condition.unit = $unit.val();
        }
        conditions.push(condition);
      });
      return {
        logic: this.ui.modal.find('.f-logic').val() || 'AND',
        conditions: conditions
      };
    },

    /** Debounced: the builder fires on every keystroke inside a value box. */
    schedulePreview: function () {
      var self = this;
      if (this.previewTimer) {
        window.clearTimeout(this.previewTimer);
      }
      this.previewTimer = window.setTimeout(function () { self.refreshPreview(); }, 350);
    },

    refreshPreview: function () {
      var $modal = this.ui.modal;
      if (!$modal.is(':visible')) { return; }

      var self = this;
      this.showingAll = false;
      MailingApi.previewSegment(JSON.stringify(this.collectFilters())).done(function (preview) {
        $modal.find('.p-count').text(Shell.formatNumber(preview.count) + ' contact(s)');
        var sample = preview.sample || [];
        $modal.find('.mlg-show-all').toggle(preview.count > sample.length)
          .text('Show all ' + Shell.formatNumber(preview.count) + ' contacts');
        $modal.find('.mlg-preview-sample').html(self.contactsTable(sample, false));
      }).fail(function (xhr) {
        $modal.find('.p-count').text(MailingApi.errorMessage(xhr));
      });
    },

    /**
     * The whole audience (capped at 5 000 by the backend) instead of the sample,
     * for the segment as it is being edited — saved or not. A later edit of a
     * condition drops back to the sample, since the list would be stale.
     */
    onShowAll: function () {
      var self = this;
      var $modal = this.ui.modal;
      var $sample = $modal.find('.mlg-preview-sample');
      $modal.find('.mlg-show-all').prop('disabled', true).text('Loading…');
      MailingApi.previewSegment(JSON.stringify(this.collectFilters()), 5000).done(function (preview) {
        self.showingAll = true;
        var all = preview.sample || [];
        $modal.find('.mlg-show-all').hide();
        $sample.html(
          '<div class="mlg-hint">' + Shell.formatNumber(all.length) + ' of ' + Shell.formatNumber(preview.count) +
            ' contact(s)' + (all.length < preview.count ? ' — the list stops at 5 000' : '') + '</div>' +
          self.contactsTable(all, true));
      }).fail(function (xhr) {
        $modal.find('.mlg-show-all').prop('disabled', false).text('Show all contacts');
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    contactsTable: function (subscribers, scrollable) {
      if (!subscribers.length) { return ''; }
      return '<div class="' + (scrollable ? 'mlg-all-contacts' : '') + '"><table class="mlg-table compact">' +
        (scrollable ? '<thead><tr><th>Name</th><th>Email</th><th>Country</th><th class="num">Turnover</th>' +
          '<th>Last order</th></tr></thead>' : '') +
        '<tbody>' + _.map(subscribers, function (subscriber) {
          var name = ((subscriber.firstName || '') + ' ' + (subscriber.lastName || '')).trim();
          return '<tr><td>' + _.escape(name || '—') + '</td>' +
            '<td>' + _.escape(subscriber.email || '') + '</td>' +
            (scrollable ? '<td>' + _.escape(subscriber.countryCode || '') + '</td>' : '') +
            '<td class="num">' + Shell.formatMoney(subscriber.totalTurnover) + '</td>' +
            '<td>' + Shell.formatDate(subscriber.lastOrder) + '</td></tr>';
        }).join('') + '</tbody></table></div>';
    },

    //----------Save / delete------------------------------------------------//

    onSave: function () {
      var self = this;
      var data = {
        name: this.ui.modal.find('.f-name').val(),
        description: this.ui.modal.find('.f-description').val(),
        filters: JSON.stringify(this.collectFilters())
      };
      if (!data.name) {
        Shell.banner(this.$el, 'Name is required', 'error');
        return;
      }
      var saving = this.editingSecId ?
          MailingApi.updateSegment(this.editingSecId, data) :
          MailingApi.createSegment(data);

      saving.done(function () {
        self.closeModal();
        Shell.banner(self.$el, 'Segment saved', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onDelete: function (e) {
      var self = this;
      var name = $(e.currentTarget).closest('tr').find('.c-name').text();
      if (!window.confirm('Delete this segment? Campaigns using it lose their audience.\n\n' + name)) { return; }

      MailingApi.deleteSegment(this.secIdOf(e)).done(function () {
        Shell.banner(self.$el, 'Segment deleted', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    closeModal: function () {
      this.editingSecId = null;
      this.ui.modal.hide().empty();
    },

    secIdOf: function (e) {
      return $(e.currentTarget).closest('tr').data('secid');
    }
  });

  module.exports = SegmentsView;
});

